import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Tag, Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

function CategoryRow({ category, onEdit, onDelete }) {
  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{category.code}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{category.name}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{category.description || '-'}</span>
      </td>
      <td>
        <span className={`badge ${category.is_active ? 'badge-success' : 'badge-danger'}`}>
          {category.is_active ? 'Aktif' : 'Nonaktif'}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(category)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(category)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CategoriesPage() {
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', description: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, [pagination.page, filters]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/categories', { params });
      if (res.data.success) {
        setCategories(res.data.data?.categories || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/categories', form);
      if (res.data.success) {
        toast.success('Kategori berhasil dibuat');
        setShowCreateModal(false);
        setForm({ code: '', name: '', description: '', is_active: true });
        fetchCategories();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/categories/${selectedCategory.id}`, form);
      if (res.data.success) {
        toast.success('Kategori berhasil diperbarui');
        setShowEditModal(false);
        setSelectedCategory(null);
        fetchCategories();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!confirm(`Yakin hapus kategori ${category.name}?`)) return;
    try {
      const res = await api.delete(`/categories/${category.id}`);
      if (res.data.success) {
        toast.success('Kategori berhasil dihapus');
        fetchCategories();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setForm({
      code: category.code || '',
      name: category.name || '',
      description: category.description || '',
      is_active: category.is_active,
    });
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen Kategori</h1>
          <p className="page-subtitle">Kelola kategori produk</p>
        </div>
        {hasPermission('create_categories') && (
          <button onClick={() => { setForm({ code: '', name: '', description: '', is_active: true }); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Tambah Kategori
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari kode, nama..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchCategories()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchCategories} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : categories.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Tag className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada kategori yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kode</th>
                    <th>Nama</th>
                    <th>Deskripsi</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <CategoryRow
                      key={cat.id}
                      category={cat}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah Kategori Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Kode <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Contoh: CAT-001" />
              </div>
              <div>
                <label className="label">Nama <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama kategori" />
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Deskripsi kategori" rows={3} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                  <option value="true">Aktif</option>
                  <option value="false">Nonaktif</option>
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCategory && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit Kategori</h3>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="label">Kode <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required />
              </div>
              <div>
                <label className="label">Nama <span className="text-danger-600">*</span></label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <label className="label">Deskripsi</label>
                <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                  <option value="true">Aktif</option>
                  <option value="false">Nonaktif</option>
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedCategory(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}